import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';

export interface AnalyticsMetrics {
  totalSessions: number;
  totalPageViews: number;
  totalEvents: number;
  bounceRate: number;
  avgSessionDuration: number;
  topPages: Array<{ page: string; views: number }>;
  topSources: Array<{ source: string; sessions: number }>;
  dailyStats: Array<{ date: string; sessions: number; pageViews: number }>;
  deviceTypes: Array<{ device: string; sessions: number }>;
  browsers: Array<{ browser: string; sessions: number }>;
  formSubmissions: number;
  topFormTypes: Array<{ form_type: string; submissions: number }>;
  leadSources: Array<{ source: string; leads: number }>;
}

const fetchAnalyticsData = async (days: number = 30): Promise<AnalyticsMetrics> => {
  const startDate = startOfDay(subDays(new Date(), days));
  const endDate = endOfDay(new Date());

  // Fetch sessions
  const { data: sessions } = await supabase
    .from('analytics_sessions')
    .select('*')
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  // Fetch events
  const { data: events } = await supabase
    .from('analytics_events')
    .select('*')
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  // Fetch form submissions
  const { data: formSubmissions } = await supabase
    .from('form_submissions')
    .select('*')
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  // Calculate metrics
  const totalSessions = sessions?.length || 0;
  const totalPageViews = events?.filter(e => e.event_type === 'page_view').length || 0;
  const totalEvents = events?.length || 0;
  
  const bouncedSessions = sessions?.filter(s => s.page_views <= 1).length || 0;
  const bounceRate = totalSessions > 0 ? (bouncedSessions / totalSessions) * 100 : 0;
  
  const avgSessionDuration = sessions?.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) / totalSessions || 0;

  // Top pages
  const pageViews: Record<string, number> = {};
  events?.filter(e => e.event_type === 'page_view').forEach(e => {
    const path = new URL(e.page_url).pathname;
    pageViews[path] = (pageViews[path] || 0) + 1;
  });
  const topPages = Object.entries(pageViews)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([page, views]) => ({ page, views }));

  // Top sources
  const sources: Record<string, number> = {};
  sessions?.forEach(s => {
    const source = s.utm_source || s.referrer || 'Direct';
    sources[source] = (sources[source] || 0) + 1;
  });
  const topSources = Object.entries(sources)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([source, sessions]) => ({ source, sessions }));

  // Daily stats
  const dailyStats: Record<string, { sessions: number; pageViews: number }> = {};
  for (let i = 0; i < days; i++) {
    const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
    dailyStats[date] = { sessions: 0, pageViews: 0 };
  }
  
  sessions?.forEach(s => {
    const date = format(new Date(s.created_at), 'yyyy-MM-dd');
    if (dailyStats[date]) {
      dailyStats[date].sessions++;
    }
  });
  
  events?.filter(e => e.event_type === 'page_view').forEach(e => {
    const date = format(new Date(e.created_at), 'yyyy-MM-dd');
    if (dailyStats[date]) {
      dailyStats[date].pageViews++;
    }
  });

  // Device types
  const devices: Record<string, number> = {};
  sessions?.forEach(s => {
    const device = s.device_type || 'Unknown';
    devices[device] = (devices[device] || 0) + 1;
  });
  const deviceTypes = Object.entries(devices)
    .map(([device, sessions]) => ({ device, sessions }));

  // Browsers
  const browsers: Record<string, number> = {};
  sessions?.forEach(s => {
    const browser = s.browser || 'Unknown';
    browsers[browser] = (browsers[browser] || 0) + 1;
  });
  const browsersData = Object.entries(browsers)
    .map(([browser, sessions]) => ({ browser, sessions }));

  // Form submissions
  const totalFormSubmissions = formSubmissions?.length || 0;
  
  const formTypes: Record<string, number> = {};
  formSubmissions?.forEach(f => {
    formTypes[f.form_type] = (formTypes[f.form_type] || 0) + 1;
  });
  const topFormTypes = Object.entries(formTypes)
    .sort(([,a], [,b]) => b - a)
    .map(([form_type, submissions]) => ({ form_type, submissions }));

  // Lead sources
  const leadSources: Record<string, number> = {};
  formSubmissions?.forEach(f => {
    const source = f.utm_source || f.referrer || 'Direct';
    leadSources[source] = (leadSources[source] || 0) + 1;
  });
  const leadSourcesData = Object.entries(leadSources)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([source, leads]) => ({ source, leads }));

  return {
    totalSessions,
    totalPageViews,
    totalEvents,
    bounceRate,
    avgSessionDuration,
    topPages,
    topSources,
    dailyStats: Object.entries(dailyStats)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, stats]) => ({ date, ...stats })),
    deviceTypes,
    browsers: browsersData,
    formSubmissions: totalFormSubmissions,
    topFormTypes,
    leadSources: leadSourcesData,
  };
};

export const useAnalyticsData = (days: number = 30) => {
  return useQuery({
    queryKey: ['analytics-data', days],
    queryFn: () => fetchAnalyticsData(days),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};